/**
 * API 工具类 - 统一处理HTTP请求和401错误拦截
 */

// 获取API基础URL
const API_BASE_URL = 'https://api.model-portfolios.com';

// 导航函数，用于在401时跳转到登录页面
let navigateFunction = null;

/**
 * 设置导航函数
 * @param {Function} navigate - React Router的navigate函数
 */
export const setNavigateFunction = (navigate) => {
  navigateFunction = navigate;
};

/**
 * 检查用户是否已登录
 * @returns {boolean} 是否已登录
 */
const isAuthenticated = () => {
  const token = localStorage.getItem('access_token');
  return !!token;
};

/**
 * 获取认证头
 * @returns {Object} 包含Authorization的请求头
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  if (token) {
    return {
      'Authorization': `Bearer ${token}`,
    };
  }
  return {};
};

/**
 * 处理401错误，清除本地存储并跳转到登录页面
 */
const handleUnauthorized = () => {
  // 清除所有认证相关的本地存储
  const authKeys = [
    'access_token',
    'token_type',
    'expires_in',
    'userName',
    'algoChainToken',
    '.issued',
    '.expires',
    'portfolioId'
  ];

  authKeys.forEach(key => {
    localStorage.removeItem(key);
  });

  // 跳转到登录页面
  if (navigateFunction) {
    navigateFunction('/login');
  } else {
    // 如果没有设置导航函数，使用window.location作为备选方案
    window.location.href = '/login';
  }
};

/**
 * 处理HTTP响应错误
 * @param {Response} response - fetch的响应对象
 * @returns {Promise} 返回响应或抛出错误
 */
const handleResponseError = async (response) => {
  // 处理401错误
  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('Unauthorized - Please login again');
  }

  // 处理其他HTTP错误
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response;
};

/**
 * 通用请求方法
 * @param {string} url - 请求URL
 * @param {Object} options - 请求选项
 * @returns {Promise} 返回响应数据
 */
const request = async (url, options = {}) => {
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  const defaultHeaders = {
    'accept': 'application/json',
    'content-type': 'application/json',
  };

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
      // 如果需要认证，添加认证头
      ...(options.requiresAuth !== false ? getAuthHeaders() : {}),
    },
  };

  try {
    const response = await fetch(fullUrl, config);
    await handleResponseError(response);

    // 根据内容类型返回相应格式的数据
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      return await response.text();
    }
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

/**
 * GET请求
 * @param {string} url - 请求URL
 * @param {Object} options - 请求选项
 * @returns {Promise} 返回响应数据
 */
export const get = (url, options = {}) => {
  return request(url, { ...options, method: 'GET' });
};

/**
 * POST请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求数据
 * @param {Object} options - 请求选项
 * @returns {Promise} 返回响应数据
 */
export const post = (url, data, options = {}) => {
  return request(url, {
    ...options,
    method: 'POST',
    body: typeof data === 'string' ? data : JSON.stringify(data),
  });
};

/**
 * POST请求（表单数据）
 * @param {string} url - 请求URL
 * @param {URLSearchParams} formData - 表单数据
 * @param {Object} options - 请求选项
 * @returns {Promise} 返回响应数据
 */
export const postForm = (url, formData, options = {}) => {
  return request(url, {
    ...options,
    method: 'POST',
    headers: {
      'accept': 'application/json',
      'content-type': 'application/x-www-form-urlencoded',
    },
    body: formData.toString(),
  });
};

/**
 * PUT请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求数据
 * @param {Object} options - 请求选项
 * @returns {Promise} 返回响应数据
 */
export const put = (url, data, options = {}) => {
  return request(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * DELETE请求
 * @param {string} url - 请求URL
 * @param {Object} options - 请求选项
 * @returns {Promise} 返回响应数据
 */
export const del = (url, options = {}) => {
  return request(url, { ...options, method: 'DELETE' });
};

/**
 * 并行请求多个API
 * @param {Array} requests - 请求数组，每个元素包含url和options
 * @returns {Promise} 返回所有响应数据的数组
 */
export const parallel = async (requests) => {
  try {
    const promises = requests.map(({ url, options = {} }) =>
      request(url, options)
    );

    return await Promise.all(promises);
  } catch (error) {
    console.error('Parallel requests failed:', error);
    throw error;
  }
};

// 默认导出
const apiUtils = {
  get,
  post,
  postForm,
  put,
  del,
  parallel,
  setNavigateFunction,
  isAuthenticated,
};

export default apiUtils;